import { z } from 'zod';
import { Ownership, Sentiment, TimestampLike } from './common.js';

export const StructuredNarrative = z.object({
  overview: z.string().default(''),
  wins: z.string().default(''),
  challenges: z.string().default(''),
  nextSteps: z.string().default(''),
  actionsThisPeriod: z.string().default(''),
});
export type StructuredNarrative = z.infer<typeof StructuredNarrative>;

export const StandingsRow = z.object({
  url: z.string().url(),
  domain: z.string(),
  // Written by generateNarrative and rendered by the portal since the first
  // release, but absent from this schema until 2026-08-03 — the schema was
  // imported nowhere, so nothing caught the drift. Both sides now import
  // these types instead of re-declaring them.
  title: z.string().default(''),
  ownership: Ownership,
  sentiment: Sentiment,
  /** Position at the start of the reporting period ("what changed this month"). */
  positionStart: z.number().int().nullable(),
  positionEnd: z.number().int().nullable(),
  delta: z.number().int().nullable(),
  /**
   * Position at the client's FIRST-EVER pull, not the period's first pull.
   * This is the number the delivery team actually reports on — a client
   * eight months in cares about progress since onboarding, not since the
   * 1st of the month. Null when the URL had no position at baseline.
   * Absent on snapshots generated before 3.7.0.
   */
  positionBaseline: z.number().int().nullable().default(null),
  /** positionEnd - positionBaseline. Null when either end is missing. */
  baselineDelta: z.number().int().nullable().default(null),
  /**
   * Mirror of the trackedUrl's `isFocus` star at generation time. Decides
   * which rows are charted; frozen with the rest of the snapshot so a later
   * focus change doesn't silently alter a published report.
   */
  isFocus: z.boolean().default(false),
  /**
   * Which keyword this row's positions came from. A URL can rank for several
   * of a client's keywords; the snapshot picks the one where it ranks best
   * at period end and takes BOTH the standings row and the chart series from
   * it, so the table and the chart can never disagree.
   */
  keywordId: z.string().default(''),
  keyword: z.string().default(''),
  /**
   * False when the row is about someone else who shares the subject's name.
   * Frozen with the rest of the snapshot. Absent on snapshots generated before
   * this field existed, and absent means "is our subject".
   */
  isSubject: z.boolean().optional(),
  /**
   * The PM's note on why this row is graded as it is. Fed to the narrative
   * generator, which cannot infer intent from a position number — "the client's
   * father, suppress but do not claim" has to be said.
   */
  notes: z.string().optional(),
});
export type StandingsRow = z.infer<typeof StandingsRow>;

export const TimePoint = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  position: z.number().int().nullable(),
  /**
   * Derived from the points either side rather than measured, because this
   * series' keyword was not pulled on this date. Renderers draw the line
   * through it but must not mark it as a data point. See alignSeriesToGrid.
   */
  interpolated: z.boolean().optional(),
});
export type TimePoint = z.infer<typeof TimePoint>;

/**
 * Page-1 make-up on a single pull date. The client-facing "is page one
 * getting better?" chart — the one number a non-technical reader can act on
 * without knowing what a position is.
 */
export const Page1CompositionPoint = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  positive: z.number().int().min(0),
  neutral: z.number().int().min(0),
  negative: z.number().int().min(0),
  /** Page-1 results with no classification yet — charted as a grey remainder. */
  unclassified: z.number().int().min(0),
});
export type Page1CompositionPoint = z.infer<typeof Page1CompositionPoint>;

/**
 * Google's entity card. Ann asked for this in the 2026-08-03 walkthrough —
 * "will it be showing the knowledge panel again?" — because a client's panel
 * is often the most prominent thing on their results page, and a report that
 * omits it doesn't look like what the client sees.
 */
export const ReportKnowledgePanel = z.object({
  title: z.string(),
  description: z.string().optional(),
  url: z.string().optional(),
  attributes: z.record(z.string()).optional(),
});
export type ReportKnowledgePanel = z.infer<typeof ReportKnowledgePanel>;

export const ReportSnapshot = z.object({
  standings: z.array(StandingsRow),
  healthScoreAtPeriodStart: z.number().min(0).max(100),
  healthScoreAtPeriodEnd: z.number().min(0).max(100),
  /** Keyed by URL. Spans baselineDate → period end, not just the period. */
  chartData: z.record(z.array(TimePoint)),
  page1Composition: z.array(Page1CompositionPoint).default([]),
  /**
   * Which keyword the composition chart describes. Composition is measured on
   * ONE keyword — the primary if the client has one — rather than summed
   * across all of them: keywords are added at different times, so a sum makes
   * page 1 appear to grow the day a second keyword starts being tracked.
   */
  page1CompositionKeyword: z.string().default(''),
  /** yyyy-mm-dd of the earliest pull the snapshot drew on. Null if none. */
  baselineDate: z.string().nullable().default(null),
  /**
   * The single location every position in this snapshot was measured in.
   * Rankings are stored one doc per (date, location); before 3.7.0 the
   * snapshot ignored locationCode entirely and could subtract a position in
   * one city from a position in another. Null only on legacy snapshots.
   */
  locationCode: z.number().int().nullable().default(null),
  locationName: z.string().default(''),
  /** The panel as it stood at period end; null when Google showed none. */
  knowledgePanel: ReportKnowledgePanel.nullable().default(null),
  /** The panel at the client's first pull, so gains/losses are visible. */
  knowledgePanelAtBaseline: ReportKnowledgePanel.nullable().default(null),
  /**
   * Whether the baseline pull could have carried a panel at all.
   *
   * SEMrush-backfilled history never captured one, so a null baseline there
   * means "we don't know", not "there wasn't one" — and reporting a panel as
   * newly won when it may have existed all along would be a lie in a client
   * document. False suppresses any gained/lost claim.
   */
  knowledgePanelBaselineKnown: z.boolean().default(false),
  /**
   * Captures in this period where the crawler stopped short of the SERP — see
   * isTruncatedCapture in ./ranking.
   *
   * A truncated capture cannot support a claim about what is NOT ranking, and
   * "the negatives are gone" is exactly the claim a reputation report makes.
   * On 2026-08-06 a capture showed 13 results for a keyword whose real SERP ran
   * to 14 pages, with the client's negative coverage at ~#14 — invisible to us
   * and, had this shipped, absent from their report. Counted so the editor can
   * refuse to present the period as complete.
   */
  truncatedCaptureCount: z.number().int().default(0),
  /**
   * Captures in this period flagged as a different SERP from the one before
   * (see isDivergentCapture in ./ranking) that no PM has reviewed yet. They are
   * left out of every figure in the snapshot; the count is what lets the editor
   * refuse to publish until someone has said whether each one is real.
   */
  suspectCaptureCount: z.number().int().default(0),
  generatedFromRankingIds: z.array(z.string()),
});
export type ReportSnapshot = z.infer<typeof ReportSnapshot>;

export const ReportDoc = z.object({
  clientId: z.string().min(1),
  subjectId: z.string().optional(),
  /**
   * Narrows the report to ONE of the subject's keywords.
   *
   * Absent means every active keyword for the subject, merged by best position
   * — which is what every report before this did, and what they keep doing.
   * New reports default to the subject's primary keyword, because that is what
   * the delivery team reported on in practice and because "which search is
   * this?" is the first question a client asks about a ranking report.
   */
  keywordId: z.string().optional(),
  title: z.string().min(1),
  periodStart: TimestampLike,
  periodEnd: TimestampLike,
  status: z.enum(['draft', 'published']),
  narrative: StructuredNarrative,
  actions: z.array(z.string()).default([]),
  uniqueSlug: z.string().min(1),
  /**
   * Which location to report on. Null means "resolve it at generation time"
   * (primary keyword's first location, else the most common one).
   */
  primaryLocationCode: z.number().int().nullable().default(null),
  primaryLocationName: z.string().default(''),
  /**
   * Per-report override of which URLs are charted. Null means "use the
   * trackedUrl isFocus stars". A per-report list exists so a PM tuning one
   * client report doesn't mutate the global focus set and silently change
   * the Rankings tab and every future report.
   */
  chartedUrls: z.array(z.string()).nullable().default(null),
  createdAt: TimestampLike,
  createdBy: z.string().min(1),
  generatedAt: TimestampLike.optional(),
  publishedAt: TimestampLike.optional(),
  publishedBy: z.string().optional(),
  lockedAt: TimestampLike.nullable().optional(),
  lockedBy: z.string().optional(),
  snapshot: ReportSnapshot.optional(),
});
export type ReportDoc = z.infer<typeof ReportDoc>;

export const ReportVersionDoc = z.object({
  version: z.number().int().min(1),
  savedAt: TimestampLike,
  savedBy: z.string().min(1),
  snapshot: ReportDoc,
});
export type ReportVersionDoc = z.infer<typeof ReportVersionDoc>;

// ---------------------------------------------------------------------
// Chart selection — shared so the portal, the staff preview and the
// snapshot builder can never disagree about which rows get a line.
// ---------------------------------------------------------------------

/**
 * Rows to chart, given the report's override list. Falls back to `isFocus`,
 * and then to the pre-3.7.0 inline rule (owned, or a negative that touched
 * page 1) so reports published before the snapshot carried `isFocus` keep
 * rendering exactly as they did.
 */
export function selectChartedRows(
  standings: StandingsRow[],
  chartedUrls?: string[] | null,
): StandingsRow[] {
  if (chartedUrls && chartedUrls.length > 0) {
    const wanted = new Set(chartedUrls);
    return standings.filter((s) => wanted.has(s.url));
  }
  if (standings.some((s) => s.isFocus)) return standings.filter((s) => s.isFocus);
  return standings.filter(
    (s) =>
      s.ownership === 'owned' ||
      (s.sentiment === 'negative' &&
        ((s.positionEnd ?? 999) <= 10 || (s.positionStart ?? 999) <= 10)),
  );
}

/**
 * Reports split their position charts by what the team is trying to DO with
 * a URL, because the two intents point opposite ways: a negative moving to a
 * bigger number is a win, an owned result moving to a smaller number is a
 * win. One mixed chart forced a caption apologising for the contradiction.
 */
export function splitByIntent(rows: StandingsRow[]): {
  suppress: StandingsRow[];
  promote: StandingsRow[];
} {
  return {
    suppress: rows.filter((s) => s.sentiment === 'negative'),
    promote: rows.filter((s) => s.sentiment !== 'negative'),
  };
}
